<template>
  <CommonAnnoucement
    v-if="isCommonAnnouncementEnabled && announcementUserId !== undefined"
    :key="announcementUserId"
    :enabled="isCommonAnnouncementEnabled"
    :user-id="announcementUserId"
    :video-src="announcementVideo"
    :media-src="announcementPoster"
    learn-more-url="https://kcalmar.gitbook.io/kcalmar-docs/baza-wiedzy/changelog-co-sie-zmienilo/21.10.2026-r.-wymiana-posilkow-w-aplikacji-mobilnej"
  />

  <div v-cloak id="app-layout" class="app-layout">
    <div class="app-container">
      <div class="app-nav" :class="[collapsed]">
        <div class="app-nav-header">
          <router-link class="app-nav-logo" :to="{ name: 'DashboardBase' }">
            <img src="@/assets/img/logo.png" alt="Kcalmar logo">
          </router-link>
          <button
            class="app-nav-hamburger"
            data-test-id="CollapseNavButton"
            @click="toggleCollapsed"
          >
            <BaseIcon size="24" variant="outline" name="keyboard_double_arrow_left" />
          </button>
          <div v-cloak class="app-nav-profile">
            <div class="app-nav-avatar">
              <div class="avatar" :style="{ backgroundImage: userAvatarUrl }" alt="Avatar" />
              <div v-if="user?.is_vip" class="app-nav-avatar-vip">
                <BaseIcon name="star" size="16" />
              </div>
            </div>
            <div class="app-nav-name">
              <a :href="`${domain}/dietetyk/account/info/`">
                {{ user?.first_name ?? '' }}
                {{ user?.last_name ?? '' }}
              </a>
            </div>
            <div class="app-nav-email">
              <a :href="`${domain}/dietetyk/account/info/`">{{ user?.email ?? '' }} </a>
            </div>
          </div>
        </div>

        <div class="content">
          <div class="app-nav-links">
            <!-- VISITS (full page link to React via proxy) -->
            <a :class="{ 'router-link-active': isMigratedPathActive('/visits') }" href="/visits">
              <BaseIcon size="24" name="calendar_today" />

              <span>Wizyty</span>

              <!-- UNREAD COUNTS -->
              <template v-if="visitsUnreadCount > 0">
                <span v-if="collapsed === 'not-collapsed'" class="visits-counter bg-visits">
                  {{ visitsUnreadCount }}
                </span>
                <div v-else class="indicator bg-visits" />
              </template>
            </a>

            <!-- PATIENTS LIST (full page link to React via proxy) -->
            <a
              :class="{ 'router-link-active': isMigratedPathActive('/patients') }"
              href="/patients"
              @click="pushDataLayer({ event: 'menu_patient' })"
            >
              <BaseIcon size="24" name="group" />
              <span>Pacjenci</span>
            </a>

            <!-- CHAT (full page link to React via proxy) -->
            <div v-if="chatEnabled" class="relative">
              <a
                :class="{ 'router-link-active': isMigratedPathActive('/chat') }"
                href="/chat"
                @click="pushDataLayer({ event: 'menu_chat' })"
              >
                <BaseIcon size="24" name="chat" />
                <span
                  v-if="collapsed === 'collapsed' && chatUnreadCount > 0"
                  class="unread-count-indicator bg-chat"
                />

                <span>Chat</span>
                <span
                  v-if="collapsed === 'not-collapsed' && chatUnreadCount > 0"
                  class="unread-count bg-chat"
                >
                  {{ chatUnreadCount }}
                </span>
              </a>
            </div>

            <!-- DIVIDER -->
            <div class="app-nav-divider" />

            <!-- MEAL PLANS -->
            <router-link
              :class="{ 'router-link-forced-inactive': route.params.dietType === 'patient' }"
              :to="{ name: 'Diet' }"
            >
              <BaseIcon size="24" name="assignment" />
              <span>Szablony</span>
            </router-link>

            <!-- DISHES -->
            <router-link :to="{ name: 'DishesList' }">
              <BaseIcon size="24" name="restaurant_menu" />
              <span>Potrawy</span>
            </router-link>

            <!-- INGREDIENTS -->
            <router-link :to="{ name: 'IngredientsList' }">
              <BaseIcon size="24" name="local_grocery_store" />
              <span>Produkty</span>
            </router-link>

            <!-- DIVIDER -->
            <div class="app-nav-divider" />

            <!-- TRAININGS -->
            <a :href="`${domain}/dietetyk/szkolenia/aplikacja/koszyk/2023/`">
              <BaseIcon size="24" name="movie_filter" />
              <span>Szkolenia</span>
            </a>

            <!--
          <a
            :href="`${domain}/dietetyk/zapytaj-dietetyka/lista/`"
            :title="
              user?.counters.questions ?? 0 > 0 ? `Nowe pytania: ${user?.counters.questions}` : ''
            "
          >
            <div class="relative">
              <BaseIcon
                size="24"
                name="sms"
              />
              <div
                v-if="user?.counters.questions ?? 0 > 0"
                class="dot"
              />
            </div>
            <span>Zapytaj dietetyka</span>
          </a>
          -->
            <!-- UNIVERSITIES -->
            <a v-if="user?.is_leader" :href="`${domain}/dietetyk/university/`">
              <BaseIcon name="school" size="24" />
              <span>Uczelnie</span>
            </a>
          </div>

          <div class="app-nav-footer app-nav-links">
            <!-- PROFILE (full page link to React via proxy) -->
            <a
              :class="['item', { 'router-link-active': isMigratedPathActive('/profile') }]"
              href="/profile"
            >
              <BaseIcon size="24" name="public" />
              <span>Profil specjalisty</span>
            </a>

            <!-- SETTINGS -->
            <a class="item" :href="`${domain}/dietetyk/account/info/`">
              <BaseIcon size="24" name="settings" />
              <span>Ustawienia</span>
            </a>

            <!-- CONTACT -->
            <div class="item" :class="{ active: showContactInfo }" @click="toggleShowContactInfo">
              <BaseIcon size="24" name="call" />
              <span>Kontakt</span>
            </div>

            <!-- KNOWLEDGE BASE -->
            <a
              class="item"
              href="https://kcalmar.gitbook.io/kcalmar-docs"
              target="_blank"
              rel="noopener noreferrer"
              title="Baza wiedzy"
            >
              <img :src="knowledgeBaseIcon" class="knowledge-base-icon" alt="">
              <span>Baza wiedzy</span>
            </a>

            <!-- DIVIDER -->
            <div class="app-nav-divider" />
            <!-- LOGOUT -->
            <AppLogout />
          </div>
        </div>
      </div>
      <div
        class="app-content"
        :class="{
          'app-content--horizontal-scroll': isHorizontalScrollRoute,
          'dark-bg': isDarkBackgroundRoute
        }"
      >
        <div
          v-if="showContactInfo"
          class="contact-info-overlay"
          :class="{ 'menu-collapsed': collapsed === 'collapsed' }"
          @click="toggleShowContactInfo"
        >
          <div class="contact-info-box" @click.stop>
            <span class="title">
              Kontakt
              <BaseIcon name="close" @click="toggleShowContactInfo" />
            </span>
            <span>
              Jesteśmy do Twojej dyspozycji<br>
              od poniedziałku do piątku<br>
              w godz. 8:00-16:00 w dni robocze.
            </span>
            <span>Twój osobisty doradca:</span>
            <span>
              Katarzyna | 782 093 159<br>
              <a href="mailto:kkolasa@kcalmar.com">kkolasa@kcalmar.com</a>
            </span>
            <span>
              +48 783 539 157<br>
              <a href="mailto:kontakt@kcalmar.com">kontakt@kcalmar.com</a>
            </span>
          </div>
        </div>
        <div class="app-workspace" :class="{ 'app-diet-workspace': isDietHorizontalScrollRoute, 'app-list-workspace': isListHorizontalScrollRoute }">
          <div class="app-content-header">
            <AppBreadcrumbs />
            <div id="app-content-header-portal" />
          </div>
          <router-view />
        </div>
      </div>
      <AppUpgradePlanModal />
      <AppMessages />
      <KcalmarNotification />
    </div>
  </div>
  <div id="base-modal-portal" />
</template>

<script lang="ts" setup>
  import { StreamChat } from 'stream-chat'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'

  import { chatService } from '@/services/chatService'
  import knowledgeBaseIcon from '@/assets/img/leftnav/book-open-01.svg?url'
  import announcementPoster from '@/assets/img/meal-exchange-announcement.png'
  import announcementVideo from '@/assets/video/meal-exchange-announcement.mp4'

  import '@/assets/css/base.css'
  import '@/assets/css/normalize.css'

  import { KcalmarNotification } from '@ui/KcalmarNotification'

  import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
  import AppLogout from '@/components/AppLogout.vue'
  import AppMessages from '@/components/AppMessages.vue'
  import AppUpgradePlanModal from '@/components/AppUpgradePlanModal.vue'
  import BaseIcon from '@/components/BaseIcon.vue'
  import CommonAnnoucement from '@/components/CommonAnnoucement.vue'
  import { useChatStore } from '@/store/chatStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { useFeatureFlags } from '@/utils/flags'
  import { pushDataLayer } from '@/utils/pushDataLayer'

  const { isCommonAnnouncementEnabled } = useFeatureFlags()

  type Collapsed = 'not-collapsed' | 'collapsed'

  const globalStore = useGlobalStore()
  const chatStore = useChatStore()
  const user = computed(() => globalStore.user)
  const announcementUserId = computed(() => user.value?.id)
  const collapsed = ref<Collapsed>(
    (localStorage.getItem('navCollapsed') ?? 'not-collapsed') as Collapsed,
  )
  const showContactInfo = ref(false)
  const userAvatarUrl = computed(() => {
    const url =
      user.value?.avatar ?? new URL('@/assets/img/leftnav/default_avatar.png', import.meta.url).href
    return `url(${url})`
  })
  const chatEnabled = computed(() => {
    return user.value?.user_flags.includes('chat_enabled')
  })
  const toggleCollapsed = () => {
    if (collapsed.value === 'not-collapsed') {
      collapsed.value = 'collapsed'
    } else {
      collapsed.value = 'not-collapsed'
    }
    localStorage.setItem('navCollapsed', collapsed.value)
  }
  const visitsUnreadCount = computed(() => {
    return user.value?.counters.visits.total ?? 0
  })
  const chatUnreadCount = computed(() => {
    return chatStore.unreadCount
  })

  const isMigratedPathActive = (path: string) => {
    const current = window.location.pathname
    return current === path || current.startsWith(`${path}/`)
  }

  const toggleShowContactInfo = () => {
    showContactInfo.value = !showContactInfo.value
  }

  const domain = import.meta.env.VUE_APP_HOST
  const route = useRoute()
  const isDarkBackgroundRoute = computed(() => {
    // refactor when more routes are added
    return route.path.includes('dietary-guidelines')
  })
  const isDietHorizontalScrollRoute = computed(
    () => route.matched.some(record => record.name === 'DietDetail'),
  )

  const isListHorizontalScrollRoute = computed(() =>
    ['DietListUser', 'DietListSystem', 'DietListUniversity', 'DishesList', 'IngredientsList']
      .includes(String(route.name)),
  )
  const isHorizontalScrollRoute = computed(
    () => isDietHorizontalScrollRoute.value || isListHorizontalScrollRoute.value,
  )

  const parseUserIdFromToken = (token: string) => {
    const [, payload] = token.split('.')

    if (!payload) throw new Error('Token is missing')

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return JSON.parse(atob(payload))?.user_id as string
  }

  onMounted(async () => {
    if (window.innerWidth < 1200) {
      collapsed.value = 'collapsed'
    }

    const token = await chatService.getToken()
    chatStore.setToken(token)

    const client = StreamChat.getInstance(import.meta.env.VUE_APP_STREAM_CHAT_API_KEY)
    const userId = parseUserIdFromToken(token)
    chatStore.setUserId(userId)

    const chatUser = await client.connectUser({ id: userId }, token)
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    chatStore.setUnreadCount(chatUser.me.unread_count ?? 0)

    client.on(event => {
      if (event.type === 'notification.message_new' && !document.hasFocus()) {
        const isNotificationAllowed = localStorage.getItem('notificationsAllowed') === 'true'
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (isNotificationAllowed && Notification) {
          new Notification('Nowa wiadomość od pacjenta', {
            body: 'Masz nową wiadomość od Twojego pacjenta w aplikacji Kcalmar',
          })
        }
      }
      if (typeof event.total_unread_count === 'number') {
        chatStore.setUnreadCount(event.total_unread_count)
      }
    })
  })
</script>

<style lang="scss">

.indicator {
  width: 10px !important;
  height: 10px !important;
  opacity: 1 !important;
  border-radius: 50%;
  position: absolute;
  top: 15%;
  right: 15%;
}

.bg-chat {
  background-color: #d92d20;
}

.bg-visits {
  background-color: #DC6803;
}

.bg-new {
  background-color: #5925DC;
}

.badge-new {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 22px;
}

.badge-new-text {
  color: #5925DC;
  font-size: 12px;
  line-height: 18px !important;
  display: inline-flex;
  gap: 4px;
  font-weight: 500;
  padding:  2px 8px;
  align-items: center;
  border-radius: var(--radius-full, 9999px);
  border: 1px solid #d9d6fe;
  background-color: #f4f3ff;
}

.app-nav.collapsed .badge-new {
  right: 25%;
  top: 25%
}

.unread-count {
  font-size: 12px;
  color: #fff;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin-left: auto !important;
  margin-right: 10px;
}

.unread-count-indicator {
  width: 10px !important;
  height: 10px !important;
  opacity: 1 !important;
  border-radius: 50%;
  position: absolute;
  top: 15%;
  right: 15%;
}

.app-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.app-container {
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.app-nav {
  z-index: 100;
  width: 220px;
  min-width: 220px;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  transition: all 0.3s ease-in-out;
}

.app-nav .content {
  display: flex;
  flex-direction: column;
  overflow: hidden auto;
  padding-bottom: 16px;
  scrollbar-width: thin;
  flex-grow: 1;
  flex-shrink: 100;
}

.app-nav .content::-webkit-scrollbar {
  width: 8px;
}

.app-nav-header {
  background: var(--color-main-100);
  height: auto;
  min-height: 204px;
  padding-bottom: 16px;
  position: relative;
  display: flex;
  overflow: hidden;
  justify-content: center;
}

.app-nav-logo {
  position: absolute;
  top: 16px;
  left: 16px;
  -webkit-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  display: block;
  width: 34px;
  height: 64px;
  transition: all 0.3s ease-in-out;
}

.app-nav-logo img {
  width: 34px;
  height: 60px;
  -webkit-user-drag: none;
  user-select: none;
}

.app-nav-hamburger {
  position: absolute;
  right: 0;
  top: 16px;
  border-radius: 4px 0 0 4px;
  display: block;
  background-color: #2aacad;
  width: 24px;
  height: 24px;
  padding: 0;
  cursor: pointer;
  border: 0;
}

.app-nav-hamburger .base-icon {
  color: #fff;
  cursor: pointer;
}

.app-nav-profile {
  padding: 81px 16px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease-in-out;
}

.app-nav-profile a {
  color: #fff;
  text-decoration: none;
}

.app-nav-avatar {
  height: 64px;
  width: 64px;
  border-radius: 29px;
  position: relative;
  transition: all 0.3s ease-in-out;
}

.app-nav-avatar a {
  position: relative;
}

.app-nav-avatar .avatar {
  width: 64px;
  height: 64px;
  background-size: cover;
  background-position: center;
  border-radius: 32px;
  overflow: hidden;
  border: 2px solid #f0f0f0;
  box-sizing: border-box;
}

.app-nav-avatar-vip {
  position: absolute;
  right: -3px;
  bottom: -2px;
  height: 20px;
  width: 20px;
  border-radius: 10px;
  background-color: var(--color-main-10);
  text-align: center;
}

.app-nav-avatar-vip .base-icon {
  font-size: 16px;
  position: relative;
  top: 2px;
  color: #facc14;
}

.app-nav-name {
  font-family: Montserrat-SemiBold;
  color: #fff;
  font-size: 14px;
  margin-top: 17px;
  line-height: 22px;
  transition: all 0.3s ease-in-out;
}

.app-nav-email {
  font-family: Montserrat-Medium;
  color: #fff;
  font-size: 12px;
  margin-top: 4px;
  line-height: 16px;
  transition: all 0.3s ease-in-out;
}

.app-nav-links {
  flex-grow: 1;
  margin-top: 16px;
}

.app-nav-divider {
  width: calc(100% - 24px);
  height: 1px;
  background-color: #E9EAEB;
  margin: 8px auto;
}

.app-nav-footer {
  margin-top: 0;
  flex-grow: 0;
}

.app-nav-footer .item {
  cursor: pointer;
}

.app-nav-footer .knowledge-base-icon {
  flex: none;
  margin: 2px;
}

.app-nav-footer .item:active .knowledge-base-icon {
  filter: brightness(0) invert(1);
}

.app-nav-links a,
.app-nav-footer .item {
  display: flex;
  height: 44px;
  font-size: 14px;
  align-items: center;
  text-decoration: none;
  color: rgb(43 43 43 / 70%);
  letter-spacing: -0.084px;
  padding-left: 10px;
  position: relative;
  box-sizing: border-box;
  white-space: nowrap;
  font-family: Montserrat-Medium;
  width: 204px;
  margin: 0 auto 2px;
  border-radius: 8px;
}

.app-nav-consultant-contact,
.app-nav-consultant-contact a {
  color: var(--color-tertiary-text);
  white-space: nowrap;
  transition: all 0.3s ease-in-out;
}

.app-content .contact-info-box a {
  text-decoration: none;
  color: inherit;
}

.app-nav-links span,
.app-nav-footer .item span {
  display: inline-block;
  margin-left: 8px;
  line-height: 1.5;
}

.app-content .contact-info-box span:nth-child(2) {
  margin-bottom: 8px;
  line-height: 1.58;
}

.app-content .contact-info-box span:nth-child(3) {
  margin-bottom: 8px;
  line-height: 1.58;
  color: var(--color-main-100);
}

.app-content .contact-info-box span:nth-child(4) {
  margin-bottom: 8px;
  line-height: 1.58;
}

.app-content .contact-info-box span:nth-child(5) {
  line-height: 1.58;
}

.app-nav-links a:hover,
.app-nav-footer .item:hover {
  background-color: #f7f7f7;
}

.app-nav-links a:active,
.app-nav-footer .item:active {
  background-color: #55bcbe;
  color: #fff;
}

.app-nav.collapsed .app-nav-links a,
.app-nav.collapsed .app-nav-footer .item {
  width: 44px;
  padding-left: 0;
  justify-content: center;
}

.app-nav-links .base-icon,
.app-nav-footer .item .base-icon {
  color: rgb(43 43 43 / 40%);
  font-size: 24px;
  cursor: pointer;
}

.app-nav-footer .item.active {
  background-color: var(--color-main-100);
  color: #fff;
}

.app-nav-links a:active .base-icon,
.app-nav-footer .item:active .base-icon {
  color: #fff;
}

.app-nav-footer .item.active .base-icon {
  color: #fff;
}

.app-nav-links a.router-link-active:not(.router-link-forced-inactive) {
  background-color: var(--color-main-100);
  color: #fff;
}

.app-nav-links a.router-link-active:not(.router-link-forced-inactive):hover,
.app-nav-footer .item.active:hover {
  background-color: var(--color-main-100);
}

.app-nav-consultant-title {
  color: var(--color-main-100);
  margin-bottom: 8px;
  white-space: nowrap;
  transition: all 0.3s ease-in-out;
}

.app-nav-consultant-contact {
  margin-bottom: 8px;
}

.app-nav-consultant-contact:last-child {
  margin-bottom: 0;
}

/* Collapsed classes */
.app-nav.collapsed {
  width: 60px;
  min-width: 60px;
}

.app-nav.collapsed .app-nav-hamburger {
  transform: rotate(180deg);
  border-radius: 0 4px 4px 0;
}

.app-nav.collapsed .app-nav-logo {
  top: 70px;
  left: 13px;
}

.app-nav.collapsed .app-nav-profile {
  padding: 154px 16px 10px;
}

.app-nav.collapsed .app-nav-avatar {
  width: 40px;
  height: 40px;
  border-radius: 40px;
}

.app-nav.collapsed .app-nav-avatar .avatar {
  width: 40px;
  height: 40px;
}

.app-nav.collapsed .app-nav-avatar-vip {
  height: 18px;
  width: 18px;
  border-radius: 9px;
}

.app-nav.collapsed .app-nav-avatar-vip .base-icon {
  font-size: 14px;
  top: 2px;
  left: 1px;
}

.app-nav.collapsed .app-nav-name {
  display: none;
}

.app-nav.collapsed .app-nav-email {
  display: none;
}

.app-nav.collapsed .app-nav-links span,
.app-nav.collapsed .app-nav-footer .item span {
  opacity: 0;
  width: 0;
  margin-left: 0;
}

.app-nav.collapsed .app-nav-consultant-title {
  opacity: 0;
}

.app-nav.collapsed .app-nav-consultant-contact {
  opacity: 0;
}

.app-content {
  position: relative;
  background: #fff;
  font-family: 'Inter Variable', Inter, sans-serif;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden auto;
  padding: 0 32px;
}

.app-content.dark-bg {
  background: #FAFAFA;
}

.app-workspace {
  display: contents;
}

// Preserve the desktop workspace on narrow screens, including the header actions.
// Seven 130px day columns plus the expanded 142px average column.
.app-diet-workspace {
  display: block;
  min-width: 1052px;
}

.app-list-workspace {
  display: block;
  min-width: 1050px;
}

.app-content--horizontal-scroll {
  overflow: auto;
  overscroll-behavior-x: contain;
}

// any tooltips rendered outside react app
div[role='tooltip'] {
  font-family: 'Inter Variable', Inter, sans-serif;
}


.app-content-header {
  position: sticky;
  left: 0;

  /* Above diet-detail sticky chrome (notices / nav z-index 6 / schedule toolbar) */
  z-index: 20;
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin: 24px 0;
  align-items: center;
}

#app-content-header-portal {
  flex-grow: 1;
  min-width: 300px;
  display: flex;
  flex-direction: row-reverse;
}

.app-content-header .app-breadcrumbs {
  max-width: 742px;
}

.app-content .contact-info-box {
  position: absolute;
  width: 286px;
  height: auto;
  background: #fff;
  border-radius: 16px;
  z-index: 9;
  bottom: 64px;
  left: 24px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  font-family: Montserrat-Medium;
  font-size: 14px;
  box-shadow: 0 1.75px 4px -1px rgb(0 0 0 / 14%);
}

.app-content .contact-info-box .title {
  font-size: 16px;
  margin-bottom: 12px;
  height: 24px;
  line-height: 24px;
  display: flex;
  justify-content: space-between;
}

.app-content .contact-info-box .title .base-icon {
  color: var(--color-primary-text);
  cursor: pointer;
}

.app-nav-links a.router-link-active:not(.router-link-forced-inactive) .base-icon {
  color: #fff;
}

.contact-info-overlay {
  position: fixed;
  inset: 0 0 0 220px;
  background-color: rgb(0 0 0 / 30%);
  z-index: 99993;
  transition: all 0.3s ease-in-out;
}

.contact-info-overlay.menu-collapsed {
  left: 60px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--color-main-100);
  position: absolute;
  right: 0;
  bottom: -1px;
  border: 2px solid #fff;
}

.relative {
  position: relative;
}

@media (width >=1366px) {
  .app-content-header .app-breadcrumbs {
    max-width: 828px;
  }
}

@media (width >=1536px) {
  .app-content-header .app-breadcrumbs {
    max-width: 998px;
  }
}

.app-content-header .app-logout {
  width: 100px;
}

.chat-app {
  .app-content {
    padding: 0;
    max-height: none;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .app-content-header {
    padding: 24px;
    margin: 0;
    background-color: #fff;
    border-bottom: 1px solid #e0e0e0;
  }

  .chat-app #root {
    max-height: calc(100vh - 72px) !important;
  }

}

.visits-counter {
  color: #fff;
  border-radius: 100%;
  width: 22px;
  height: 22px;
  font-size: 12px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 8px;
}
</style>
